import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { EnumSortDirection } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { IContratoCliente } from '@model/ContratoCliente';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { IRelatorioCancelamentoFilter, useRelatorioCancelamentoService } from '@services/Relatorios/CancelamentoService';
import { addMonth, firstDayOfTheMonth, lastDayOfTheMonth } from '@utils/Functions';
import { yup } from '@validations/Yup';
import React from 'react';
import { RelatorioCancelamentoFilter } from './Filter';
import { RelatorioCancelamentoTable } from './Table';

export type RelatorioCancelamentoProps = {
}

export function RelatorioCancelamento (props: RelatorioCancelamentoProps) {
    const todayLastMonth = addMonth(new Date(), - 1);

    const data = useData<IContratoCliente, IRelatorioCancelamentoFilter>({
        filter: {
            dataCancelamentoDe: useField(firstDayOfTheMonth(todayLastMonth)),
            dataCancelamentoAte: useField(lastDayOfTheMonth(todayLastMonth)),
            whatsappEnvio: useField()
        },

        sortField: {
            name: "contratoClienteId",
            direction: EnumSortDirection.Asc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const relatorioCancelamentoService = useRelatorioCancelamentoService();

    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const validation = useValidation<IRelatorioCancelamentoFilter>({
        dataCancelamentoDe: yup.date().required(),
        dataCancelamentoAte: yup.date().required(),
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            data.filter.clearErrors();
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioCancelamentoService.exportar(data.requestedData)
                    .catch(message.showBlobException)
                    .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
            }
    }, [data.filter, data.requestedData, loading, message.showBlobException, relatorioCancelamentoService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            data.filter.clearErrors();
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioCancelamentoService.listar(data.requestedData)
                        .then(data.handleResultResponse)
                        .catch(message.showException)
                        .finally(loading.stop);
                        })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        remetenteWhatsappCombo.loadOptions();
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Cancelamentos" />
                </PageTitle>

                <PageContent>
                    <RelatorioCancelamentoFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />
                    
                    <RelatorioCancelamentoTable 
                        data={data}
                        isLoading={loading.isLoading}
                        onCsvButtonClick={handleCsvButtonClick}
                    />
                </PageContent>
            </Main>
        </React.Fragment>
    )
}